import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  InputAdornment,
  Typography,
  Container,
  CircularProgress,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  useTheme,
  useMediaQuery,
  Divider,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Paper,
  Checkbox,
  FormControlLabel,
  Link,
  Alert,
  AlertTitle,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Phone as PhoneIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Flag as FlagIcon,
  ArrowBack as ArrowBackIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SchoolIcon from '@mui/icons-material/School';
import { useSnackbar } from 'notistack';
import Logo from '../../../components/Logo';
import Iconify from '../../../components/Iconify';
import ResponsiveDialog from '../../../components/ResponsiveDialog';
import axios from '../../../utils/axios';
import { setSession } from '../../../utils/jwt';
import { PATH_USER, PATH_ADMIN, PATH_KIOSK, PATH_AUTH } from '../../../routes/paths';
import { getFullDomain } from '../../../utils/fullDomain';
import useAuth from '../../../hooks/useAuth';
import CompanySelectionSection from './CompanySelectionSection';
import InviteOnlySignupPanel from './InviteOnlySignupPanel';

import {
  CONSENT_REQUIRED_MESSAGE,
  ConsentChecklist,
  ConsentNotice,
  consentPayload,
  hasRequiredConsents,
} from '../../../components/legal';

const EnhancedOTPLogin = () => {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpReceived, setOtpReceived] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [emailOtpInput, setEmailOtpInput] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [step, setStep] = useState('phone-otp');
  const [error, setError] = useState('');
  const [userData, setUserData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    company_name: '',
  });

  // Email login states
  const [loginMethod, setLoginMethod] = useState('otp');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [emailCheckStep, setEmailCheckStep] = useState('enter_email');
  const [emailAccountStatus, setEmailAccountStatus] = useState(null);

  // Account conflict handling states
  const [showAccountFoundDialog, setShowAccountFoundDialog] = useState(false);
  const [existingUserDetails, setExistingUserDetails] = useState(null);
  const [conflictType, setConflictType] = useState(null);
  const [showEmailOtpDialog, setShowEmailOtpDialog] = useState(false);
  const [showPasswordCreateDialog, setShowPasswordCreateDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [mobileVerified, setMobileVerified] = useState(false);
  const [showNewPasswordFields, setShowNewPasswordFields] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const fullDomain = getFullDomain();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [subdomain, setSubdomain] = useState('');
  const { user, isAuthenticated, login } = useAuth();
  const [compExists, setCompExists] = useState(false);
  const [campusList, setCampusList] = useState([]);
  const [showCampusSelector, setShowCampusSelector] = useState(false);
  const [showCampusSelect, setShowCampusSelect] = useState(false);
  const [showChangeMobileDialog, setShowChangeMobileDialog] = useState(false);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [campusInfo, setCampusInfo] = useState(null);
  const [showMobileBanner, setShowMobileBanner] = useState(true);
  const [showDifferentRoleDialog, setShowDifferentRoleDialog] = useState(false);
  const [differentRoleDetails, setDifferentRoleDetails] = useState(null);
  const [isRegistrationFlow, setIsRegistrationFlow] = useState(false);
  // Blocks the auth-context auto-redirect while we hold the user on this page
  // for campus selection after a password login.
  const redirectHoldRef = useRef(false);

  // Consent is recorded server side against the notice version that was on
  // screen, so this only has to carry what the member ticked. Starts empty:
  // every box is unticked until the member acts on it.
  const [consents, setConsents] = useState({});
  const [consentError, setConsentError] = useState('');

  const [themeColors, setThemeColors] = useState({
    primary: '#919EAB', // neutral gray
    secondary: '#637381', // neutral gray
    loaded: false,
  });

  const checkEmailExists = async (email, mobile) => {
    try {
      const response = await axios.post('/api/v1/accounts/user/check-email-exists/', {
        email,
        mobile,
        fullDomain,
      });
      return response.data;
    } catch (err) {
      console.error('Error checking email:', err);
      return { email_exists: false };
    }
  };

  // Send email OTP. otpType must match on send and verify, and tells the
  // backend what the verification authorizes (linking a mobile vs a password).
  const sendEmailOTP = async (email, otpType = 'verification') => {
    try {
      await axios.post('/api/v1/accounts/user/send-email-otp/', {
        email,
        otp_type: otpType,
        fullDomain: window.location.hostname,
      });
      enqueueSnackbar('Email OTP sent successfully', { variant: 'success' });
      return true;
    } catch (err) {
      enqueueSnackbar(err?.error || 'Failed to send email OTP', { variant: 'error' });
      return false;
    }
  };

  // Verify email OTP
  const verifyEmailOTP = async (email, otp, otpType = 'verification') => {
    try {
      await axios.post('/api/v1/accounts/user/verify-email-otp/', {
        email,
        otp,
        otp_type: otpType,
        fullDomain: window.location.hostname,
      });
      enqueueSnackbar('Email verified successfully', { variant: 'success' });
      return true;
    } catch (err) {
      enqueueSnackbar(err?.error || 'Invalid email OTP', { variant: 'error' });
      return false;
    }
  };

  // Send the mobile OTP for account linking. Not the same as request-otp:
  // the number being claimed does not belong to any account yet, so the code
  // is authorized by the just-verified email instead.
  const sendLinkMobileOTP = async (email, mobile) => {
    try {
      await axios.post('/api/v1/accounts/user/request-link-mobile-otp/', {
        email,
        mobile,
        fullDomain,
      });
      return true;
    } catch (err) {
      console.error('Mobile OTP request failed:', err);
      enqueueSnackbar(err?.error || 'Failed to send mobile OTP', { variant: 'error' });
      return false;
    }
  };

  // Check email for password during email login
  const checkEmailForPassword = async (email) => {
    try {
      const response = await axios.post('/api/v1/accounts/user/check-email-password/', { email, fullDomain });
      return response.data;
    } catch (err) {
      console.error('Error checking email:', err);
      return { user_exists: false };
    }
  };

  // Handle change mobile number
  const handleChangeMobile = async () => {
    try {
      setLoading(true);
      const formattedOldPhone = phoneNumber.replace(/\D/g, '');
      const formattedNewPhone = newPhoneNumber.replace(/\D/g, '');

      await axios.post('/api/v1/accounts/user/change-mobile/', {
        old_mobile: formattedOldPhone,
        new_mobile: formattedNewPhone,
        slug: subdomain,
      });

      setPhoneNumber(newPhoneNumber);
      setShowChangeMobileDialog(false);
      setShowOtpInput(true);
      setOtpInput('');

      enqueueSnackbar('Mobile number changed successfully', { variant: 'success' });
    } catch (err) {
      console.error('API Error:', err);
      enqueueSnackbar(err?.error || 'Failed to change mobile number', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getActiveStep = () => {
    if (step === 'phone-otp' && !showOtpInput) return 0;
    if (step === 'phone-otp' && showOtpInput) return 1;
    return 2;
  };

  // The operator closed open signup: members arrive through a company invite.
  const inviteOnly = campusInfo?.member_signup_mode === 'invite_only';

  const handleBack = () => {
    if (step === 'registration') {
      setStep('phone-otp');
      setShowOtpInput(false);
      setOtpInput('');
    }
  };

  const fetchCampusList = async () => {
    try {
      const response = await axios.get(`/api/v1/accounts/user/list-org-campuses/?slug=${subdomain}`);
      setCampusList(response.data.campuses);
    } catch (err) {
      console.error('Error fetching campus list:', err);
      enqueueSnackbar('Failed to fetch campus list', { variant: 'error' });
    }
  };

  const handleRequestOTP = async () => {
    const formattedPhone = phoneNumber.replace(/\D/g, '');

    // Mid-link resend: the number is not on any account yet, so it has to go
    // through the linking endpoint or it comes back as "user doesn't exist".
    if (emailVerified && conflictType === 'mobile_to_email' && existingUserDetails?.email) {
      setLoading(true);
      const sent = await sendLinkMobileOTP(existingUserDetails.email, formattedPhone);
      if (sent) enqueueSnackbar('OTP sent successfully', { variant: 'success' });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await axios.post('/api/v1/accounts/user/request-otp/', {
        mobile: formattedPhone,
        fullDomain,
      });
      setIsRegistrationFlow(false);

      setShowOtpInput(true);
      enqueueSnackbar('OTP sent successfully', { variant: 'success' });
    } catch (err) {
      console.error('API Error:', err);
      if (err.error === "User with this mobile doesn't exists") {
        setStep('registration');
        enqueueSnackbar(
          inviteOnly ? 'No account found for this number.' : 'Please complete registration to continue',
          { variant: 'info' }
        );
      } else {
        // Anything else (SMS failure, throttle, server error) is not proof the
        // number is unregistered. Sending them to signup would fork an
        // existing member into the account-claim flow.
        enqueueSnackbar(err.error || err.message || 'Something went wrong. Please try again.', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Check email status and determine next step
  const handleEmailCheck = async () => {
    try {
      setLoading(true);
      setError('');

      const emailCheck = await checkEmailForPassword(loginEmail.toLowerCase());
      setEmailAccountStatus(emailCheck);

      // Check for different role
      if (emailCheck.different_role) {
        // The API returns no name to an unauthenticated caller.
        setDifferentRoleDetails({ email: loginEmail.toLowerCase() });
        setShowDifferentRoleDialog(true);
        return;
      }

      // Continue with normal flow
      if (!emailCheck.user_exists) {
        setEmailCheckStep('create_account');
      } else if (emailCheck.has_password) {
        setEmailCheckStep('enter_password');
      } else {
        setExistingUserDetails({ email: loginEmail.toLowerCase() });
        setConflictType('password_creation');
        const emailSent = await sendEmailOTP(loginEmail.toLowerCase());
        if (emailSent) {
          setShowEmailOtpDialog(true);
        }
      }
    } catch (err) {
      console.error('Email check error:', err);
      setError('Failed to check email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle email login with password
  const handleEmailPasswordLogin = async () => {
    try {
      setLoading(true);
      setError('');

      redirectHoldRef.current = true;
      const loggedInUser = await login(loginEmail.toLowerCase(), loginPassword);
      enqueueSnackbar('Login successful', { variant: 'success' });

      // Rescue accounts that ended up without any campus (email-only signups
      // from before the campus-selection fix): make them pick one now instead
      // of landing on a broken dashboard.
      const hasCampus = loggedInUser?.userAllCampus?.length > 0;
      if (loggedInUser?.role === 'user' && !hasCampus) {
        await fetchCampusList();
        setShowCampusSelector(true);
      } else {
        window.location.href = PATH_USER.dashboard;
      }
    } catch (err) {
      redirectHoldRef.current = false;
      console.error('Login Error:', err);
      const errorMessage =
        typeof err === 'object' ? err?.detail || err?.message || 'Invalid email or password' : String(err);
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle registration from email flow
  const handleEmailRegistration = () => {
    setStep('registration');
    setUserData({ ...userData, email: loginEmail });
    setLoginMethod('otp');
  };

  const updateFavicon = (faviconUrl) => {
    if (!faviconUrl) return;

    const favicons = document.querySelectorAll("link[rel*='icon']");
    favicons.forEach((favicon) => {
      favicon.href = faviconUrl;
    });

    if (favicons.length === 0) {
      const newFavicon = document.createElement('link');
      newFavicon.rel = 'icon';
      newFavicon.href = faviconUrl;
      document.head.appendChild(newFavicon);
    }
  };

  const getCampusInfo = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/v1/accounts/whitelabel/info/${fullDomain}/`);

      if (response.data && Object.keys(response.data).length > 0) {
        setCampusInfo(response.data);
        localStorage.setItem('campusInfo', JSON.stringify(response.data));
        updateFavicon(response.data.favicon);
        setSubdomain(response.data.slug);

        if (response.data.primary_colour || response.data.secondary_colour) {
          const colours = {
            primary_colour: response.data.primary_colour || '#8A03C3',
            secondary_colour: response.data.secondary_colour || '#6A02AA',
          };
          localStorage.setItem('colours', JSON.stringify(colours));

          setThemeColors({
            primary: response.data.primary_colour || '#8A03C3',
            secondary: response.data.secondary_colour || '#6A02AA',
            loaded: true,
          });
        }
      }
    } catch (err) {
      const errorMessage = err.message || 'An unexpected error occurred while fetching campus info.';
      setError(errorMessage);
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubdomain = async (fullDomain) => {
    try {
      const response = await axios.get(`/api/v1/accounts/whitelabel/info/${fullDomain}/`);
      setSubdomain(response.data.slug);
    } catch (error) {
      console.error('Error fetching organization info:', error);
    }
  };

  useEffect(() => {
    const fullDomain = getFullDomain();
    // if (fullDomain && !fullDomain.endsWith('deskos.net')) {
    fetchSubdomain(fullDomain);
    // }
  }, []);

  useEffect(() => {
    const cached = localStorage.getItem('campusInfo');
    const cachedColours = localStorage.getItem('colours');

    if (cached) {
      const parsedCache = JSON.parse(cached);
      setCampusInfo(parsedCache);
      updateFavicon(parsedCache.favicon);
    }

    if (cachedColours) {
      const parsedColours = JSON.parse(cachedColours);
      setThemeColors({
        primary: parsedColours.primary_colour || '#8A03C3',
        secondary: parsedColours.secondary_colour || '#6A02AA',
        loaded: true,
      });
      setLoading(false);
    }

    getCampusInfo();
  }, []);

  useEffect(() => {
    if (redirectHoldRef.current) return;
    if (user?.role === 'org') {
      navigate(PATH_ADMIN.dashboard, { replace: true });
    }
    if (user?.role === 'user') {
      navigate(PATH_USER.dashboard, { replace: true });
    }
    if (user?.role === 'kiosk') {
      navigate(PATH_KIOSK, { replace: true });
    }
  }, [user, isAuthenticated, navigate]);

  const handleVerifyOTP = async () => {
    try {
      setLoading(true);
      setError('');

      const formattedPhone = phoneNumber.replace(/\D/g, '');

      const response = await axios.post('/api/v1/accounts/user/verify-otp/', {
        mobile: formattedPhone,
        otp: otpInput,
        fullDomain,
      });

      if (showCampusSelect) {
        await fetchCampusList();
        setShowCampusSelector(true);
        const {accessToken} = response.data;
        setSession(accessToken);
      } else {
        const {accessToken} = response.data;
        setSession(accessToken);
        window.location.href = PATH_USER.dashboard;
      }
    } catch (err) {
      console.error('API Error:', err);
      enqueueSnackbar(err?.message || err?.error || 'Verification failed', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCampusSelection = async (campusId) => {
    try {
      setLoading(true);
      const response = await axios.post('/api/v1/accounts/user/select-campus/', {
        campus_id: campusId,
      });

      window.location.href = PATH_USER.dashboard;
    } catch (err) {
      console.error('Error selecting campus:', err);
      enqueueSnackbar('Failed to select campus', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Modified handleRegistration to check for email conflicts
  const handleRegistration = async () => {
    // Checked here so the member sees why nothing happened, and checked again
    // on the server because a checkbox is a claim and the record is ours.
    if (!hasRequiredConsents(campusInfo, consents)) {
      setConsentError(CONSENT_REQUIRED_MESSAGE);
      return;
    }
    setConsentError('');

    try {
      setLoading(true);

      const formattedPhone = phoneNumber.replace(/\D/g, '');
      const emailCheck = await checkEmailExists(userData.email, formattedPhone);

      // ADD THIS NEW CHECK for different role
      if (emailCheck.different_role) {
        setDifferentRoleDetails({ email: userData.email });
        setShowDifferentRoleDialog(true);
        setLoading(false);
        return;
      }

      if (emailCheck.email_exists) {
        setExistingUserDetails({
          email: userData.email,
          has_mobile: emailCheck.has_mobile,
          existing_mobile: emailCheck.existing_mobile,
        });
        setConflictType('mobile_to_email');
        setShowAccountFoundDialog(true);
        setLoading(false);
        return;
      }

      const payload = {
        mobile: formattedPhone,
        ...userData,
        slug: subdomain,
        // Declines travel too: an operator has to be able to show the member
        // was asked about marketing and said no.
        consents: consentPayload(campusInfo, consents),
      };

      if (userData.company_id) {
        delete payload.company_name;
        payload.company_id = userData.company_id;
      }

      const res = await axios.post('/api/v1/accounts/user/create/', payload);

      if (res.data.email_otp_sent) {
        // Email-only signup — verify via email OTP then create password
        setLoginEmail(userData.email);
        setExistingUserDetails({ email: userData.email, first_name: userData.first_name, last_name: userData.last_name });
        setConflictType('password_creation');
        setShowEmailOtpDialog(true);
        if (res.data.show_campus_selector) setShowCampusSelect(true);
        enqueueSnackbar('Verification code sent to your email.', { variant: 'success' });
      } else {
        if (res.data.show_campus_selector) {
          setShowCampusSelect(true);
        }
        setIsRegistrationFlow(true);
        setStep('phone-otp');
        setShowOtpInput(true);
        enqueueSnackbar('Registration successful! Please verify your mobile number.', { variant: 'success' });
      }
    } catch (err) {
      if (err.error === 'EMAIL_CONFLICT') {
        setExistingUserDetails(err.existing_user);
        setConflictType('mobile_to_email');
        setShowAccountFoundDialog(true);
      } else {
        enqueueSnackbar(err?.error || 'Registration failed', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle account confirmation for mobile-to-email conflict
  const handleAccountConfirmation = async (isTheirAccount) => {
    if (!isTheirAccount) {
      setShowAccountFoundDialog(false);
      enqueueSnackbar('Please use a different email address', { variant: 'info' });
      return;
    }

    if (conflictType === 'mobile_to_email') {
      const emailSent = await sendEmailOTP(existingUserDetails.email, 'account_linking');
      if (emailSent) {
        setShowAccountFoundDialog(false);
        setShowEmailOtpDialog(true);
      }
    } else if (conflictType === 'password_creation') {
      const emailSent = await sendEmailOTP(existingUserDetails.email);
      if (emailSent) {
        setShowAccountFoundDialog(false);
        setShowEmailOtpDialog(true);
      }
    }
  };

  // Handle email OTP verification and account linking
  const handleEmailOTPVerification = async () => {
    try {
      setLoading(true);

      const isLinking = conflictType === 'mobile_to_email';
      const email = isLinking ? existingUserDetails.email : loginEmail.toLowerCase();
      const verified = await verifyEmailOTP(email, emailOtpInput, isLinking ? 'account_linking' : 'verification');

      if (!verified) {
        setLoading(false);
        return;
      }

      setEmailVerified(true);
      setShowEmailOtpDialog(false);

      if (conflictType === 'mobile_to_email') {
        const sent = await sendLinkMobileOTP(email, phoneNumber.replace(/\D/g, ''));
        if (sent) {
          setShowOtpInput(true);
          setStep('phone-otp');
          enqueueSnackbar('Mobile OTP sent. Please verify to link your accounts.', { variant: 'info' });
        }
      } else if (conflictType === 'password_creation') {
        setShowPasswordCreateDialog(true);
      }
    } catch (err) {
      enqueueSnackbar('Email OTP verification failed', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle mobile OTP verification for account linking
  const handleMobileOTPVerification = async () => {
    try {
      setLoading(true);

      if (emailVerified && conflictType === 'mobile_to_email') {
        const response = await axios.post('/api/v1/accounts/user/link-mobile-to-account/', {
          email: existingUserDetails.email,
          mobile: phoneNumber.replace(/\D/g, ''),
          mobile_otp: otpInput,
          fullDomain: window.location.hostname,
        });

        const {accessToken} = response.data;
        setSession(accessToken);
        enqueueSnackbar('Accounts linked successfully!', { variant: 'success' });
        window.location.href = PATH_USER.dashboard;
      } else {
        const formattedPhone = phoneNumber.replace(/\D/g, '');
        const response = await axios.post('/api/v1/accounts/user/verify-otp/', {
          mobile: formattedPhone,
          otp: otpInput,
          fullDomain,
        });

        if (showCampusSelect) {
          await fetchCampusList();
          setShowCampusSelector(true);
          const {accessToken} = response.data;
          setSession(accessToken);
        } else {
          const {accessToken} = response.data;
          setSession(accessToken);
          window.location.href = PATH_USER.dashboard;
        }
      }
    } catch (err) {
      console.error('Mobile OTP verification failed:', err);
      enqueueSnackbar(err?.error || err?.message || 'Mobile OTP verification failed', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle password creation
  const handlePasswordCreation = async () => {
    try {
      if (newPassword !== confirmPassword) {
        enqueueSnackbar('Passwords do not match', { variant: 'error' });
        return;
      }

      if (newPassword.length < 8) {
        enqueueSnackbar('Password must be at least 8 characters long', { variant: 'error' });
        return;
      }

      setLoading(true);

      const email = conflictType === 'password_creation' ? loginEmail.toLowerCase() : existingUserDetails.email;
      const response = await axios.post('/api/v1/accounts/user/set-password/', {
        email,
        password: newPassword,
        fullDomain: window.location.hostname,
      });

      const {accessToken} = response.data;
      setSession(accessToken);
      enqueueSnackbar('Password created successfully!', { variant: 'success' });

      // Email-only signups reach here without ever seeing the campus selector,
      // so gate the redirect the same way the mobile-OTP path does. Also treat
      // a token payload with no campuses as needing selection, which rescues
      // accounts that previously slipped through without one.
      const hasCampus = response.data.user?.userAllCampus?.length > 0;
      if (showCampusSelect || !hasCampus) {
        setShowPasswordCreateDialog(false);
        await fetchCampusList();
        setShowCampusSelector(true);
      } else {
        window.location.href = PATH_USER.dashboard;
      }
    } catch (err) {
      if (err.error === 'UNIQUE constraint failed: accounts_customuser.email') {
        enqueueSnackbar('This email is already registered. Please signup with a different email or contact support.', {
          variant: 'error',
        });
      } else if (err.error === 'UNIQUE constraint failed: accounts_customuser.mobile') {
        enqueueSnackbar(
          'This mobile number is already registered. Please signup with a different mobile or contact support.',
          { variant: 'error' }
        );
      } else {
        enqueueSnackbar('There was an error while trying to sign you up. Please try again.', { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Reset all states
  const resetStates = () => {
    setShowAccountFoundDialog(false);
    setShowEmailOtpDialog(false);
    setShowPasswordCreateDialog(false);
    setShowDifferentRoleDialog(false); // ADD THIS
    setEmailVerified(false);
    setMobileVerified(false);
    setExistingUserDetails(null);
    setConflictType(null);
    setEmailOtpInput('');
    setNewPassword('');
    setConfirmPassword('');
    setEmailCheckStep('enter_email');
    setEmailAccountStatus(null);
    setDifferentRoleDetails(null); // ADD THIS
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone.replace(/\D/g, ''));
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const LogoSection = () => {
    if (loading && !campusInfo && !themeColors.loaded) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
          <CircularProgress sx={{ color: themeColors.primary }} />
        </Box>
      );
    }

    if (campusInfo?.logo) {
      return (
        <Box
          component="img"
          src={campusInfo.logo}
          alt="Campus Logo"
          sx={{
            width: { xs: 170, sm: 220 },
            height: { xs: 76, sm: 100 },
            objectFit: 'contain',
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.style.display = 'none';
          }}
        />
      );
    }

    return <Logo />;
  };

  const getButtonStyles = (variant = 'contained') => {
    if (!themeColors.loaded) {
      return {};
    }

    if (variant === 'contained') {
      return {
        bgcolor: themeColors.primary,
        '&:hover': {
          bgcolor: themeColors.secondary,
        },
        '&:disabled': {
          bgcolor: 'rgba(0, 0, 0, 0.12)',
        },
      };
    }

    if (variant === 'outlined') {
      return {
        borderColor: themeColors.primary,
        color: themeColors.primary,
        '&:hover': {
          borderColor: themeColors.secondary,
          bgcolor: 'rgba(0, 0, 0, 0.04)',
        },
      };
    }

    if (variant === 'text') {
      return {
        color: themeColors.primary,
        '&:hover': {
          bgcolor: 'rgba(0, 0, 0, 0.04)',
        },
      };
    }
  };

  // Common text field focus styles
  const getTextFieldStyles = () => {
    if (!themeColors.loaded) {
      return {};
    }

    return {
      '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
          borderColor: themeColors.primary,
        },
      },
      '& .MuiInputLabel-root.Mui-focused': {
        color: themeColors.primary,
      },
    };
  };

  return (
    <Container
      maxWidth="sm"
      sx={{
        py: { xs: 2, sm: 4 },
        // The 80px bottom gap was reserving space for an app banner that is no
        // longer rendered, so on a phone it was just dead scroll.
        pb: { xs: 3, sm: 12 },
      }}
    >
      <Card
        elevation={3}
        sx={{
          borderRadius: 2,
          boxShadow: (theme) => theme.shadows[isMobile ? 0 : 3],
          // Was a hard 100vh, which on a phone always overflowed the viewport
          // by the container padding and left the form half scrolled.
          height: 'auto',
          ...(themeColors.loaded && {
            borderTop: `4px solid ${themeColors.primary}`,
          }),
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          {/* Header Section */}
          <Box sx={{ mb: { xs: 2, sm: 4 }, position: 'relative' }}>
            {step === 'registration' && (
              <IconButton
                onClick={handleBack}
                sx={{
                  position: 'absolute',
                  left: -8,
                  top: -8,
                  ...(themeColors.loaded && {
                    color: themeColors.primary,
                  }),
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: { xs: 2.5, sm: 5 },
                width: '100%',
              }}
            >
              <LogoSection />
            </Box>
          </Box>

          {/* Main Content */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {step === 'phone-otp' && loginMethod === 'otp' && (
              <>
                <Typography variant="h6" textAlign="center" gutterBottom>
                  {showOtpInput ? 'Enter Verification Code' : 'Login with Phone'}
                </Typography>

                {!showOtpInput ? (
                  <>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Mobile Number"
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      disabled={loading}
                      error={phoneNumber && !validatePhoneNumber(phoneNumber)}
                      helperText={
                        phoneNumber && !validatePhoneNumber(phoneNumber)
                          ? 'Please enter a valid 10-digit mobile number'
                          : ''
                      }
                      sx={getTextFieldStyles()}
                      InputProps={{
                        inputProps: { inputMode: 'numeric' },
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                              <Iconify icon="emojione:flag-for-india" />
                              <Typography sx={{ ml: 1 }}>+91</Typography>
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleRequestOTP}
                      disabled={loading || !validatePhoneNumber(phoneNumber)}
                      sx={{ py: 1.5, ...getButtonStyles('contained') }}
                    >
                      {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Get OTP'}
                    </Button>

                    <Box sx={{ textAlign: 'center', mt: 1 }}>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => setLoginMethod('email')}
                        sx={{
                          ...(themeColors.loaded && { color: themeColors.primary }),
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        Login with Email
                      </Link>
                    </Box>
                  </>
                ) : (
                  <>
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        We've sent a verification code to +91 {phoneNumber}
                      </Typography>

                      <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                          if (isRegistrationFlow) {
                            setShowChangeMobileDialog(true);
                          } else {
                            setShowOtpInput(false);
                            setOtpInput('');
                          }
                        }}
                        sx={{
                          mt: 1, // Adds margin top to separate from text
                          textDecoration: 'underline',
                          textTransform: 'none',
                          ...getButtonStyles('text'),
                        }}
                      >
                        {isRegistrationFlow ? 'Change mobile number' : 'Go back'}
                      </Button>
                    </Box>

                    {emailVerified && conflictType === 'mobile_to_email' && (
                      <Alert
                        severity="info"
                        sx={{
                          mb: 2,
                          ...(themeColors.loaded && {
                            '& .MuiAlert-icon': {
                              color: themeColors.primary,
                            },
                          }),
                        }}
                      >
                        <AlertTitle>Email Verified!</AlertTitle>
                        Now enter the mobile OTP to link your accounts.
                      </Alert>
                    )}

                    <TextField
                      fullWidth
                      label="Enter OTP"
                      variant="outlined"
                      value={otpInput}
                      onChange={(e) => setOtpInput(e.target.value)}
                      sx={getTextFieldStyles()}
                      inputProps={{
                        maxLength: 6,
                        inputMode: 'numeric',
                        autoComplete: 'one-time-code',
                        style: { textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.2em' },
                      }}
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={
                        emailVerified && conflictType === 'mobile_to_email'
                          ? handleMobileOTPVerification
                          : handleVerifyOTP
                      }
                      disabled={loading || otpInput.length !== 6}
                      sx={{ py: 1.5, ...getButtonStyles('contained') }}
                    >
                      {loading ? (
                        <CircularProgress size={24} sx={{ color: 'white' }} />
                      ) : emailVerified && conflictType === 'mobile_to_email' ? (
                        'Link Accounts'
                      ) : (
                        'Verify & Sign In'
                      )}
                    </Button>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary" display="inline">
                        Didn't receive the code?
                      </Typography>
                      <Button
                        variant="text"
                        onClick={handleRequestOTP}
                        disabled={loading}
                        sx={{ ml: 1, ...getButtonStyles('text') }}
                      >
                        Resend
                      </Button>
                    </Box>
                  </>
                )}
              </>
            )}

            {/* Email Login Section */}
            {step === 'phone-otp' && loginMethod === 'email' && (
              <>
                <Typography variant="h6" textAlign="center" gutterBottom>
                  Login with Email
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {emailCheckStep === 'enter_email' && (
                  <>
                    <TextField
                      fullWidth
                      label="Email address"
                      variant="outlined"
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      error={loginEmail && !validateEmail(loginEmail)}
                      helperText={loginEmail && !validateEmail(loginEmail) ? 'Please enter a valid email address' : ''}
                      sx={getTextFieldStyles()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleEmailCheck}
                      disabled={loading || !validateEmail(loginEmail)}
                      sx={{ py: 1.5, mt: 1, ...getButtonStyles('contained') }}
                    >
                      {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Continue'}
                    </Button>
                  </>
                )}

                {emailCheckStep === 'enter_password' && (
                  <>
                    <Box
                      sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: 'grey.50',
                        borderRadius: 1,
                        ...(themeColors.loaded && {
                          borderLeft: `4px solid ${themeColors.primary}`,
                        }),
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Welcome back!
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{ ...(themeColors.loaded && { color: themeColors.primary }) }}
                      >
                        {loginEmail}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {loginEmail}
                      </Typography>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                          setEmailCheckStep('enter_email');
                          setLoginEmail('');
                          setLoginPassword('');
                          setError('');
                        }}
                        sx={{ mt: 1, textTransform: 'none', ...getButtonStyles('text') }}
                      >
                        Not you? Use different email
                      </Button>
                    </Box>

                    <TextField
                      fullWidth
                      label="Password"
                      variant="outlined"
                      type={showLoginPassword ? 'text' : 'password'}
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      autoFocus
                      sx={getTextFieldStyles()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Iconify icon="material-symbols:password" width={24} height={24} color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton onClick={() => setShowLoginPassword(!showLoginPassword)} edge="end">
                              <Iconify
                                icon={showLoginPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'}
                                width={24}
                                height={24}
                              />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            sx={{
                              ...(themeColors.loaded && {
                                color: themeColors.primary,
                                '&.Mui-checked': {
                                  color: themeColors.primary,
                                },
                              }),
                            }}
                          />
                        }
                        label="Remember me"
                      />

                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => navigate(PATH_AUTH.resetPassword)}
                        sx={{
                          ...(themeColors.loaded && { color: themeColors.primary }),
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        Forgot password?
                      </Link>
                    </Box>

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleEmailPasswordLogin}
                      disabled={loading || !loginPassword}
                      sx={{ py: 1.5, mt: 1, ...getButtonStyles('contained') }}
                    >
                      {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Sign In'}
                    </Button>
                  </>
                )}

                {emailCheckStep === 'create_account' && (
                  <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        No account found
                      </Typography>
                      <Typography variant="body2">
                        We couldn't find an account with <strong>{loginEmail}</strong>. Would you like to create a new
                        account?
                      </Typography>
                    </Alert>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleEmailRegistration}
                        sx={{ py: 1.5, ...getButtonStyles('contained') }}
                      >
                        Create Account
                      </Button>

                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setEmailCheckStep('enter_email');
                          setLoginEmail('');
                          setError('');
                        }}
                        sx={{ py: 1.5, ...getButtonStyles('outlined') }}
                      >
                        Try Different Email
                      </Button>
                    </Box>
                  </>
                )}

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => {
                      setLoginMethod('otp');
                      setError('');
                      setLoginEmail('');
                      setLoginPassword('');
                      setEmailCheckStep('enter_email');
                      setEmailAccountStatus(null);
                    }}
                    sx={{
                      ...(themeColors.loaded && { color: themeColors.primary }),
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    Login with OTP
                  </Link>
                </Box>
              </>
            )}

            {step === 'registration' && inviteOnly && (
              <InviteOnlySignupPanel
                slug={subdomain}
                colour={themeColors.loaded ? themeColors.primary : undefined}
                onBack={handleBack}
              />
            )}

            {step === 'registration' && !inviteOnly && (
              <>
                <Typography variant="h6" textAlign="center" gutterBottom>
                  Complete Your Profile
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="First Name"
                      variant="outlined"
                      value={userData.first_name}
                      onChange={(e) => setUserData({ ...userData, first_name: e.target.value })}
                      sx={getTextFieldStyles()}
                    />
                    <TextField
                      fullWidth
                      label="Last Name"
                      variant="outlined"
                      value={userData.last_name}
                      onChange={(e) => setUserData({ ...userData, last_name: e.target.value })}
                      sx={getTextFieldStyles()}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    label="Email"
                    variant="outlined"
                    type="email"
                    value={userData.email}
                    onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                    error={userData.email && !validateEmail(userData.email)}
                    helperText={
                      userData.email && !validateEmail(userData.email) ? 'Please enter a valid email address' : ''
                    }
                    sx={getTextFieldStyles()}
                  />
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Mobile Number"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    error={phoneNumber && !validatePhoneNumber(phoneNumber)}
                    helperText={
                      phoneNumber && !validatePhoneNumber(phoneNumber)
                        ? 'Please enter a valid 10-digit mobile number'
                        : ''
                    }
                    sx={getTextFieldStyles()}
                    InputProps={{
                      inputProps: { inputMode: 'numeric' },
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                            <Iconify icon="emojione:flag-for-india" />
                            <Typography sx={{ ml: 1 }}>+91</Typography>
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <CompanySelectionSection userData={userData} setUserData={setUserData} subdomain={subdomain} />
                  <ConsentChecklist
                    org={campusInfo}
                    value={consents}
                    onChange={(next) => {
                      setConsents(next);
                      if (consentError) setConsentError('');
                    }}
                    error={consentError}
                    colour={themeColors.loaded ? themeColors.primary : undefined}
                    disabled={loading}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleRegistration}
                    disabled={loading || !userData.first_name || !userData.email || !validateEmail(userData.email)}
                    sx={{ py: 1.5, mt: 1, ...getButtonStyles('contained') }}
                  >
                    {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Complete Registration'}
                  </Button>
                </Box>
              </>
            )}

            {step !== 'registration' && (
              <ConsentNotice
                org={campusInfo}
                action="signing in"
                colour={themeColors.loaded ? themeColors.primary : undefined}
                sx={{ mt: 3 }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Account Found Dialog */}
      <ResponsiveDialog open={showAccountFoundDialog} maxWidth="sm" fullWidth onClose={() => { }} disableEscapeKeyDown>
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Avatar
            sx={{
              mx: 'auto',
              mb: 2,
              ...(themeColors.loaded ? { bgcolor: themeColors.primary } : { bgcolor: 'warning.main' }),
            }}
          >
            <WarningIcon />
          </Avatar>
          <Typography variant="h6">Account Found</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
            We found an existing account with the email address you entered:
          </Typography>
          <Box
            sx={{
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              mb: 2,
              ...(themeColors.loaded && {
                borderLeft: `4px solid ${themeColors.primary}`,
              }),
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {existingUserDetails?.email}
            </Typography>
            {existingUserDetails?.has_mobile && (
              <Typography variant="body2" color="text.secondary">
                Mobile: {existingUserDetails?.existing_mobile}
              </Typography>
            )}
          </Box>
          <Typography variant="body2" textAlign="center">
            Is this your account?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
          <Button onClick={() => handleAccountConfirmation(false)} variant="outlined" sx={getButtonStyles('outlined')}>
            No, use different email
          </Button>
          <Button onClick={() => handleAccountConfirmation(true)} variant="contained" sx={getButtonStyles('contained')}>
            Yes, this is my account
          </Button>
        </DialogActions>
      </ResponsiveDialog>


      {/* Email OTP Dialog */}
      <ResponsiveDialog open={showEmailOtpDialog} maxWidth="sm" fullWidth onClose={() => { }} disableEscapeKeyDown>
        <DialogTitle sx={{ textAlign: 'center' }}>
          <Avatar
            sx={{
              mx: 'auto',
              mb: 2,
              ...(themeColors.loaded ? { bgcolor: themeColors.primary } : { bgcolor: 'primary.main' }),
            }}
          >
            <EmailIcon />
          </Avatar>
          <Typography variant="h6">Verify Email</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3, textAlign: 'center' }}>
            We've sent a verification code to{' '}
            <Typography component="span" fontWeight="bold">
              {conflictType === 'mobile_to_email' ? existingUserDetails?.email : loginEmail}
            </Typography>
          </Typography>
          <TextField
            fullWidth
            label="Enter Email OTP"
            variant="outlined"
            value={emailOtpInput}
            onChange={(e) => setEmailOtpInput(e.target.value)}
            sx={getTextFieldStyles()}
            inputProps={{
              maxLength: 6,
              inputMode: 'numeric',
              autoComplete: 'one-time-code',
              style: { textAlign: 'center', letterSpacing: '0.5em', fontSize: '1.2em' },
            }}
          />
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary" display="inline">
              Didn't receive the code?
            </Typography>
            <Button
              variant="text"
              onClick={() =>
                conflictType === 'mobile_to_email'
                  ? sendEmailOTP(existingUserDetails?.email, 'account_linking')
                  : sendEmailOTP(loginEmail)
              }
              disabled={loading}
              sx={{ ml: 1, ...getButtonStyles('text') }}
            >
              Resend
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={() => {
              setShowEmailOtpDialog(false);
              resetStates();
            }}
            variant="outlined"
            sx={getButtonStyles('outlined')}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEmailOTPVerification}
            variant="contained"
            disabled={loading || emailOtpInput.length !== 6}
            sx={getButtonStyles('contained')}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Verify Email'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* Password Creation Dialog */}
      <ResponsiveDialog open={showPasswordCreateDialog} maxWidth="sm" fullWidth onClose={() => { }} disableEscapeKeyDown>
        <DialogTitle sx={{ textAlign: 'center' }}>
          <Avatar
            sx={{
              mx: 'auto',
              mb: 2,
              ...(themeColors.loaded ? { bgcolor: themeColors.primary } : { bgcolor: 'success.main' }),
            }}
          >
            <CheckCircleIcon />
          </Avatar>
          <Typography variant="h6">Create Password</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3, textAlign: 'center' }}>
            Email verified! Please create a password for your account.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="New Password"
              variant="outlined"
              type={showNewPasswordFields ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText="Password must be at least 8 characters long"
              sx={getTextFieldStyles()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNewPasswordFields(!showNewPasswordFields)} edge="end">
                      <Iconify
                        icon={showNewPasswordFields ? 'eva:eye-fill' : 'eva:eye-off-fill'}
                        width={24}
                        height={24}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirm Password"
              variant="outlined"
              type={showNewPasswordFields ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={confirmPassword && newPassword !== confirmPassword}
              helperText={confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
              sx={getTextFieldStyles()}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button
            onClick={handlePasswordCreation}
            variant="contained"
            disabled={loading || !newPassword || newPassword !== confirmPassword || newPassword.length < 8}
            fullWidth
            sx={getButtonStyles('contained')}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Create Password & Login'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* Campus Selector Dialog */}
      <ResponsiveDialog
        open={showCampusSelector}
        fullWidth
        maxWidth="sm"
        disableEscapeKeyDown
        disableBackdropClick
        PaperProps={{
          sx: {
            borderRadius: { xs: '16px 16px 0 0', sm: 2 },
            maxHeight: { xs: '92vh', sm: '90vh' },
            ...(themeColors.loaded && {
              borderTop: `4px solid ${themeColors.primary}`,
            }),
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: 'center',
            pb: 3,
            pt: 3,
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" component="h2" fontWeight="500">
            Select Your Campus
          </Typography>
          <Typography variant="subtitle1" sx={{ mt: 1, color: 'text.secondary' }}>
            Choose the campus you'd like to access
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <List sx={{ py: 0 }}>
            {Array.isArray(campusList) &&
              campusList.map((campus, index) => (
                <Box key={campus.id}>
                  <ListItem
                    sx={{
                      mb: 0,
                      p: 2,
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'stretch', sm: 'center' },
                      gap: { xs: 1.5, sm: 0 },
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        transform: 'translateX(8px)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="h6" component="h3">
                          {campus.name}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                          <LocationOnIcon
                            sx={{
                              mr: 1,
                              color: 'text.secondary',
                              fontSize: 20,
                            }}
                          />
                          <Typography variant="body1" color="text.secondary">
                            {campus.city}
                          </Typography>
                        </Box>
                      }
                      sx={{ mr: { xs: 0, sm: 2 } }}
                    />

                    <Button
                      variant="contained"
                      onClick={() => handleCampusSelection(campus.id)}
                      disabled={loading}
                      sx={{
                        minWidth: 100,
                        height: 40,
                        width: { xs: '100%', sm: 'auto' },
                        textTransform: 'none',
                        borderRadius: 2,
                        boxShadow: 'none',
                        '&:hover': {
                          boxShadow: 'none',
                        },
                        ...getButtonStyles('contained'),
                      }}
                    >
                      {loading ? <CircularProgress size={24} color="inherit" /> : 'Select'}
                    </Button>
                  </ListItem>
                  {index < campusList.length - 1 && <Divider sx={{ my: 0 }} />}
                </Box>
              ))}
          </List>
        </DialogContent>
      </ResponsiveDialog>

      {/* Different Role Dialog */}
      <ResponsiveDialog
        open={showDifferentRoleDialog}
        maxWidth="sm"
        fullWidth
        onClose={() => setShowDifferentRoleDialog(false)}
      >
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Avatar
            sx={{
              mx: 'auto',
              mb: 2,
              bgcolor: 'warning.main',
            }}
          >
            <InfoIcon />
          </Avatar>
          <Typography variant="h6">Account Type Mismatch</Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Different Account Type</AlertTitle>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Seems like you have already signed up with this email ID but not as a user.
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              If you are an Admin, please visit your admin link to login.
            </Typography>
            <Typography variant="body2">
              Thank you.
            </Typography>
          </Alert>

          {differentRoleDetails && (
            <Box
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 1,
                borderLeft: '4px solid',
                borderColor: 'warning.main',
              }}
            >
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Account Details:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {differentRoleDetails.email}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, flexDirection: 'column', gap: 1 }}>
          <Button
            onClick={() => {
              setShowDifferentRoleDialog(false);
              if (step === 'registration') {
                setUserData({ ...userData, email: '' });
              } else {
                setLoginEmail('');
                setEmailCheckStep('enter_email');
              }
            }}
            variant="contained"
            sx={getButtonStyles('contained')}
            fullWidth
          >
            OK, Use Different Email
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* Change Mobile Dialog */}
      <ResponsiveDialog open={showChangeMobileDialog} onClose={() => setShowChangeMobileDialog(false)} fullWidth>
        <DialogTitle>Change Mobile Number</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            variant="outlined"
            label="New Mobile Number"
            type="tel"
            value={newPhoneNumber}
            onChange={(e) => setNewPhoneNumber(e.target.value)}
            sx={{ mt: 2, ...getTextFieldStyles() }}
            error={newPhoneNumber && !validatePhoneNumber(newPhoneNumber)}
            helperText={
              newPhoneNumber && !validatePhoneNumber(newPhoneNumber)
                ? 'Please enter a valid 10-digit mobile number'
                : ''
            }
            InputProps={{
              inputProps: { inputMode: 'numeric' },
              startAdornment: (
                <InputAdornment position="start">
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                    <Iconify icon="emojione:flag-for-india" />
                    <Typography sx={{ ml: 1 }}>+91</Typography>
                  </Box>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowChangeMobileDialog(false)} sx={getButtonStyles('text')}>
            Cancel
          </Button>
          <Button
            onClick={handleChangeMobile}
            disabled={loading || !validatePhoneNumber(newPhoneNumber)}
            variant="contained"
            sx={getButtonStyles('contained')}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Change Number'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </Container>
  );
};

export default EnhancedOTPLogin;
